// ============================================
// moldClaw Web - Devices Management Page
// ============================================

import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';

interface Device {
  id: string;
  name: string | null;
  platform: string | null;
  last_seen_at: string;
  is_current: boolean;
}

interface License {
  code: string;
  tier: string;
  status: string;
  max_devices: number;
  devices_used: number;
}

const LICENSE_SERVER_URL = import.meta.env.VITE_LICENSE_SERVER_URL || 
  'https://license.moldclaw.com/api/v1';

export function DevicesPage() {
  const { session } = useAuth();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [selectedLicense, setSelectedLicense] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // 라이선스 목록 로드
  useEffect(() => {
    if (!session?.access_token) return;
    
    const loadLicenses = async () => {
      try {
        const response = await fetch(`${LICENSE_SERVER_URL}/sync/user-licenses`, {
          headers: {
            'X-Supabase-Token': session.access_token,
          },
        });
        
        const data = await response.json();
        
        if (data.success) {
          setLicenses(data.licenses);
          if (data.licenses.length > 0) {
            setSelectedLicense(data.licenses[0].code);
          }
        }
      } catch (err) {
        setError('라이선스 목록을 불러올 수 없습니다');
      } finally {
        setLoading(false);
      }
    };
    
    loadLicenses();
  }, [session?.access_token]);

  // 기기 목록 로드
  useEffect(() => {
    if (!selectedLicense || !session?.access_token) return;
    
    const loadDevices = async () => {
      setDevicesLoading(true);
      try {
        // 웹에서는 Supabase 직접 접근 또는 별도 API 필요
        // 현재는 mock 데이터
        setDevices([]);
        setError(null);
      } catch (err) {
        setError('기기 목록을 불러올 수 없습니다');
      } finally {
        setDevicesLoading(false);
      }
    };
    
    loadDevices();
  }, [selectedLicense, session?.access_token]);

  const handleRemoveDevice = async (deviceId: string) => {
    if (!confirm('이 기기를 제거하시겠습니까?\n해당 기기에서는 다시 로그인해야 합니다.')) {
      return;
    }
    
    setRemovingId(deviceId);
    // API 호출 로직 추가 필요
    setRemovingId(null);
  };

  const getPlatformIcon = (platform: string | null) => {
    switch (platform?.toLowerCase()) {
      case 'windows': return '🪟';
      case 'macos': return '🍎';
      case 'linux': return '🐧';
      default: return '💻';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ko-KR');
  };

  const selectedLicenseData = licenses.find(l => l.code === selectedLicense);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500/30 border-t-orange-500 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            기기 관리
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            등록된 기기를 관리하세요. 기기 제한에 도달하면 기존 기기를 제거해야 합니다.
          </p>
        </div>

        {/* 라이선스 없음 */}
        {licenses.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center shadow-sm">
            <div className="text-5xl mb-4">📱</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              등록된 라이선스가 없습니다
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              moldClaw 앱에서 라이선스를 활성화하면 여기에 표시됩니다.
            </p>
            <a
              href="/pricing"
              className="inline-block px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
            >
              플랜 구매하기
            </a>
          </div>
        )}

        {/* 라이선스 선택 */}
        {licenses.length > 0 && (
          <>
            {licenses.length > 1 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  라이선스 선택
                </label>
                <select
                  value={selectedLicense || ''}
                  onChange={(e) => setSelectedLicense(e.target.value)}
                  className="w-full md:w-auto px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                >
                  {licenses.map((license) => (
                    <option key={license.code} value={license.code}>
                      {license.code} ({license.tier.toUpperCase()})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 라이선스 정보 카드 */}
            {selectedLicenseData && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">라이선스 코드</p>
                    <p className="font-mono text-lg text-gray-900 dark:text-white">
                      {selectedLicenseData.code}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">플랜</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      selectedLicenseData.tier === 'pro' 
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        : selectedLicenseData.tier === 'team'
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {selectedLicenseData.tier.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">기기 사용량</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {selectedLicenseData.devices_used} / {selectedLicenseData.max_devices}
                    </p>
                  </div>
                </div>

                {/* 진행 바 */}
                <div className="mt-4">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all ${
                        selectedLicenseData.devices_used >= selectedLicenseData.max_devices
                          ? 'bg-red-500'
                          : 'bg-orange-500'
                      }`}
                      style={{ 
                        width: `${(selectedLicenseData.devices_used / selectedLicenseData.max_devices) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 에러 */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* 기기 목록 */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  등록된 기기
                </h2>
              </div>

              {devicesLoading && (
                <div className="p-8 text-center">
                  <div className="animate-spin w-6 h-6 border-2 border-orange-500/30 border-t-orange-500 rounded-full mx-auto" />
                </div>
              )}

              {!devicesLoading && devices.length === 0 && (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <p>이 라이선스에 등록된 기기가 없습니다.</p>
                  <p className="text-sm mt-1">moldClaw 앱에서 라이선스를 활성화하세요.</p>
                </div>
              )}

              {!devicesLoading && devices.length > 0 && (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {devices.map((device) => (
                    <div key={device.id} className="px-6 py-4 flex items-center gap-4">
                      <div className="text-2xl">
                        {getPlatformIcon(device.platform)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900 dark:text-white">
                            {device.name || '이름 없는 기기'}
                          </p>
                          {device.is_current && (
                            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                              현재 기기
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          마지막 접속: {formatDate(device.last_seen_at)}
                        </p>
                      </div>
                      {!device.is_current && (
                        <button
                          onClick={() => handleRemoveDevice(device.id)}
                          disabled={removingId === device.id}
                          className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {removingId === device.id ? '제거 중...' : '제거'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 안내 */}
            <div className="mt-6 text-sm text-gray-500 dark:text-gray-400 space-y-1">
              <p>• 현재 기기는 이 웹 페이지에서 제거할 수 없습니다.</p>
              <p>• 기기를 제거하면 해당 기기에서 다시 로그인해야 합니다.</p>
              <p>• 더 많은 기기가 필요하시면 플랜을 업그레이드하세요.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DevicesPage;
